import { useEffect } from 'react';
import { APIError, ClientError, useErrorHandler, useQueryClient } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import { addPrzejscie, fetchDostepneTkd } from 'api/strefy/strefy';
import { PrzejscieModEnum, StrefyErrorCode } from 'api/strefy/strefy.enum';
import { StrefyErrorResponse } from 'api/strefy/strefy.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useModalState } from 'hooks/useModalState/useModalState';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useQuery } from 'hooks/useQuery/useQuery';
import { AppMessages } from 'i18n/messages';

import { AddPassage } from './AddPassage';
import { AddPassageContainerProps, AddPassageFormBody } from './AddPassage.types';
import { useAddPassageValidation } from './AddPassage.validation';

const EMPTY_FORM: AddPassageFormBody = {
  rcpId: '',
  tkdId: '',
  mod: '',
  strefaPoczatkowaId: '',
  strefaKoncowaId: '',
};

// Reader-side refusals of POST /przejscie (contract §2): the reader list in the modal was stale.
const READER_ERROR_MESSAGES: Partial<Record<StrefyErrorCode, string>> = {
  [StrefyErrorCode.PRZEJSCIE_DUPLIKAT]: 'definitions.passages.add.error.duplicate',
  [StrefyErrorCode.PRZEJSCIE_TKD_STATUS]: 'definitions.passages.add.error.readerStatus',
  [StrefyErrorCode.PRZEJSCIE_TKD_SEKWENCJA]: 'definitions.passages.add.error.readerSequence',
  [StrefyErrorCode.TKD_NIE_ISTNIEJE]: 'definitions.passages.add.error.readerNotFound',
};

export const AddPassageContainer = ({
  servers,
  zones,
  defaultServerId,
  isDisabled,
  disabledReason,
}: AddPassageContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { handleError } = useErrorHandler();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { schema } = useAddPassageValidation();

  const methods = useForm<AddPassageFormBody>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const rcpId = methods.watch('rcpId');

  const { data: readersResponse, isInitialLoading: isLoadingReaders } = useQuery(
    [QueryKeyEnum.PRZEJSCIE_DOSTEPNE_TKD, rcpId],
    fetchDostepneTkd,
    {
      args: { rcpId },
      enabled: isModalOpen && !!rcpId,
    },
  );

  useEffect(() => {
    if (!isModalOpen) return;

    methods.reset({ ...EMPTY_FORM, rcpId: defaultServerId || servers[0]?.rcpId || '' });
  }, [isModalOpen]);

  const closeWithToast = (messageId: string) => {
    toast.dark(formatMessage({ id: messageId }));
    onModalClose();
  };

  const { mutate: onAddPassage, isLoading } = useMutation(addPrzejscie, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIA]);
      queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIE_SERWERY]);
      queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIE_DOSTEPNE_TKD]);
      queryClient.invalidateQueries([QueryKeyEnum.STREFY]);
      queryClient.invalidateQueries([QueryKeyEnum.MONITORING_OBECNOSCI]);
      closeWithToast(AppMessages['definitions.passages.add.success']);
    },
    onError: (error: ClientError<APIError<StrefyErrorResponse>>) => {
      const code = error.response?.data.code;

      if (code === StrefyErrorCode.MONITORING_AKTYWNY) {
        queryClient.invalidateQueries([QueryKeyEnum.MONITORING_OBECNOSCI]);
        closeWithToast(AppMessages['definitions.passages.monitoringActive']);
        return;
      }

      if (code === StrefyErrorCode.PRZEJSCIE_ZA_MALO_STREF) {
        queryClient.invalidateQueries([QueryKeyEnum.STREFY]);
        closeWithToast(AppMessages['definitions.passages.add.tooFewZones']);
        return;
      }

      if (code === StrefyErrorCode.PRZEJSCIE_TE_SAME_STREFY) {
        methods.setError('strefaKoncowaId', {
          message: formatMessage({ id: AppMessages['definitions.passages.add.sameZones'] }),
        });
        return;
      }

      const readerErrorMessage = code && READER_ERROR_MESSAGES[code];
      if (readerErrorMessage) {
        queryClient.invalidateQueries([QueryKeyEnum.PRZEJSCIE_DOSTEPNE_TKD]);
        methods.setValue('tkdId', '');
        methods.setValue('mod', '');
        methods.setError('tkdId', { message: formatMessage({ id: readerErrorMessage }) });
        return;
      }

      if (code === StrefyErrorCode.STREFA_NIE_ISTNIEJE) {
        queryClient.invalidateQueries([QueryKeyEnum.STREFY]);
      }

      handleError({ error });
    },
  });

  const onSubmit = ({ tkdId, mod, strefaPoczatkowaId, strefaKoncowaId }: AddPassageFormBody) => {
    onAddPassage({ tkdId, mod: mod as PrzejscieModEnum, strefaPoczatkowaId, strefaKoncowaId });
  };

  return (
    <FormProvider {...methods}>
      <AddPassage
        servers={servers}
        zones={zones}
        readers={readersResponse?.data || []}
        isLoadingReaders={isLoadingReaders}
        isAllReadersDefined={!!readersResponse && !readersResponse.data.length}
        isDisabled={isDisabled}
        disabledReason={disabledReason}
        isLoading={isLoading}
        onSubmit={onSubmit}
        isModalOpen={isModalOpen}
        onModalOpen={onModalOpen}
        onModalClose={onModalClose}
      />
    </FormProvider>
  );
};
